import React, { useState } from 'react'
import { useMedia } from 'react-use'
import { Tag } from '@overdose/components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { CreateProjectBookMark } from 'shared-types'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { Client } from '~/customClients/client'

import { useSession } from '../../hooks'
import BookmarkButton from '../BookmarkButton/BookmarkButton'
import DifficultyRating from '../DifficultyRating/DifficultyRating'
import { ProjectTitleProps } from './ProjectTitle.types'

const ProjectTitle = ({
  title,
  difficulty,
  category,
  className,
  isBookmarked,
  projectId,
}: ProjectTitleProps) => {
  const { session } = useSession()
  const queryClient = useQueryClient()

  const isTabletScreen = useMedia('(min-width: 768px)', false)

  const [bookmarkActive, setBookmarkActive] = useState(isBookmarked)

  const toggleBookmarkProject = () => {
    setBookmarkActive(!bookmarkActive)
  }

  const { mutateAsync: updateProjectBookmark } = useMutation(
    async (payload: Omit<CreateProjectBookMark, 'productIds'>) => {
      return Client.projectList.updateProjectBookmark(payload, session)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['bookmark-project'] })
      },
    }
  )

  const handleOnBookMarkProject = () => {
    toggleBookmarkProject()
    updateProjectBookmark({
      name: title,
      projectId,
      status: isBookmarked,
    })
  }
  return (
    <div
      className={classNames(
        className,
        'w-full flex items-center justify-between'
      )}>
      <div
        className={classNames('flex flex-col justify-center gap-6 sm:gap-4')}>
        <Typography tag={TypographyTag.h1} variant={TypographyVariant.Heading4}>
          {title}
        </Typography>
        <div className={classNames('flex', 'gap-4', 'flex-wrap')}>
          <Typography
            className={classNames('flex', 'gap-2', 'items-center')}
            tag={TypographyTag.div}
            variant={TypographyVariant.BodyLargeBold}>
            Difficulty <DifficultyRating difficulty={difficulty} />
          </Typography>
          <div
            className={classNames('flex', 'gap-2', 'flex-wrap')}
            style={
              {
                '--tag-default-color-bg':
                  'var(--color-text-primary-blue, #0C254C)',
                '--tag-default-color-text': 'white',
              } as React.CSSProperties
            }>
            <Tag size='sm' variant='default'>
              <Typography
                className={classNames('uppercase')}
                tag={TypographyTag.span}
                variant={TypographyVariant.BodySmall}>
                {category.name}
              </Typography>
            </Tag>
          </div>
        </div>
      </div>
      <div className={classNames('self-end', 'md:self-center')}>
        <BookmarkButton
          onClickToggle={handleOnBookMarkProject}
          active={bookmarkActive}
          size={isTabletScreen ? 'lg' : 'sm'}
        />
      </div>
    </div>
  )
}

export default ProjectTitle
